/**
 * Derived values of a sent schedule node (BA-85 design, "Derived values"). Everything the
 * panel and the report show is computed here from the node; the dashboard stores nothing
 * but a per-browser Dismiss and, for a failed send, the name of the action it replaced.
 */

import { CurrentAction } from '../types/currentAction';
import { ScheduleType, StepData } from '../types/schedule';
import { SentSchedule } from '../types/sentSchedule';

/** The tablet's heartbeat is every 5 min: after 11 min without one it counts as offline (PO O1) */
export const TABLET_OFFLINE_AFTER_MS = 11 * 60 * 1000;

/** A choice board shows at most this many choices (BA-87) */
export const MAX_CHOICES = 3;

/** "Replaced by X": X started within this long of the end (both server times) */
const REPLACE_WINDOW_MS = 5000;

export type SentStep = StepData & { id: string };

export const isTabletOffline = (lastSeen: number | null, now: number): boolean =>
  lastSeen !== null && now - lastSeen > TABLET_OFFLINE_AFTER_MS;

export const isChoiceBoard = (node: SentSchedule): boolean => node.schedule?.type === 'choice_board';

/** The snapshot's steps by `order`; a choice board's first 3 */
export function orderedSteps(node: SentSchedule): SentStep[] {
  const steps = Object.entries(node.schedule?.steps ?? {})
    .map(([id, step]) => ({ ...step, id }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return isChoiceBoard(node) ? steps.slice(0, MAX_CHOICES) : steps;
}

/** "Step n of N": progress.currentStep is 0-based */
export function stepPosition(node: SentSchedule): { n: number; total: number } {
  const total = orderedSteps(node).length;
  const current = node.progress?.currentStep ?? 0;
  return { n: Math.min(Math.max(current, 0), Math.max(total - 1, 0)) + 1, total };
}

export const currentStepOf = (node: SentSchedule): SentStep | undefined =>
  orderedSteps(node)[stepPosition(node).n - 1];

export const doneCount = (node: SentSchedule): number =>
  Object.keys(node.progress?.stepsDone ?? {}).length;

export type RowStatus =
  | { kind: 'done' | 'skipped'; at: number }
  | { kind: 'now' }
  | { kind: 'notDone' }
  | { kind: 'blank' };

/** A step row: done / skipped with its time, the live current row, or blank (live) / not done (report) */
export function rowStatus(node: SentSchedule, step: SentStep, index: number, live: boolean): RowStatus {
  const done = node.progress?.stepsDone?.[step.id];
  if (done) {
    return { kind: done.skipped ? 'skipped' : 'done', at: done.doneAt };
  }
  if (live) {
    return index === (node.progress?.currentStep ?? 0) ? { kind: 'now' } : { kind: 'blank' };
  }
  return { kind: 'notDone' };
}

/** The latest of sentAt, every doneAt and endedAt */
export function lastUpdate(node: SentSchedule): number {
  const times = [node.sentAt, node.endedAt ?? 0,
    ...Object.values(node.progress?.stepsDone ?? {}).map((d) => d.doneAt ?? 0)];
  return Math.max(...times);
}

/** C2 and C3 apply only to these: board steps finish only with the ✓ (n3, BA-88) */
const STEP_CONDITION_TYPES: readonly ScheduleType[] = ['routine', 'waiting_board'];

const conditionsApply = (node: SentSchedule): boolean =>
  STEP_CONDITION_TYPES.includes(node.schedule?.type);

/** C2: the step waits for a grown-up's PIN on the tablet */
export const isParentStep = (node: SentSchedule, step: SentStep | undefined): boolean =>
  !!step && conditionsApply(node) && step.completionAction === 'parent_only';

/** C3: the step's timer in whole minutes, or null when it has none or it does not apply */
export function waitingMinutes(node: SentSchedule, step: SentStep | undefined): number | null {
  const seconds = step?.timer?.durationSeconds;
  if (!step || !conditionsApply(node) || !seconds || seconds <= 0) {
    return null;
  }
  return Math.max(1, Math.round(seconds / 60));
}

/** C7b: on a choice board the chosen item is the single stepsDone entry (M2) */
export function chosenStep(node: SentSchedule): { step: SentStep; at: number } | null {
  if (!isChoiceBoard(node)) {
    return null;
  }
  const done = Object.entries(node.progress?.stepsDone ?? {});
  if (done.length !== 1) {
    return null;
  }
  const [id, entry] = done[0];
  const data = node.schedule?.steps?.[id];
  return data ? { step: { ...data, id }, at: entry.doneAt } : null;
}

/** (scheduleId, sentAt) identifies a send; 'none' when there is no node */
export const identityOf = (node: SentSchedule | null | undefined): string =>
  node ? `${node.scheduleId}@${node.sentAt}` : 'none';

/**
 * After a timeout or a network error: did the send land? A node of this schedule whose
 * identity differs from the one seen before the POST. Never compares clocks (M3).
 */
export const sendLanded = (before: string, scheduleId: string, node: SentSchedule | null): boolean =>
  !!node && node.scheduleId === scheduleId && identityOf(node) !== before;

/** Who replaced an ended node: the current action, or a newer sent schedule */
export function replacedByName(
  node: SentSchedule,
  currentAction: CurrentAction | null,
  actionName: string | undefined,
  newer: SentSchedule | null,
): string | null {
  if (node.endedBy !== 'replaced' || !node.endedAt) {
    return null;
  }
  if (currentAction && actionName && currentAction.startTimestamp >= node.endedAt - REPLACE_WINDOW_MS) {
    return actionName;
  }
  if (newer && newer !== node && Math.abs(newer.sentAt - node.endedAt) <= REPLACE_WINDOW_MS) {
    return newer.schedule?.name ?? null;
  }
  return null;
}

/** C11: the schedule a failed send replaced, if the previous node says so */
export function failedReplacedSchedule(node: SentSchedule, previous: SentSchedule | null): string | null {
  if (!previous || previous.endedBy !== 'replaced' || !previous.endedAt) {
    return null;
  }
  return Math.abs(previous.endedAt - node.sentAt) <= REPLACE_WINDOW_MS ? previous.schedule?.name ?? null : null;
}

/**
 * The report card under the "now" card: the ended sentSchedule, or, while a new one is
 * active, the one it replaced. Neither once dismissed in this browser.
 */
export function reportNode(
  sent: SentSchedule | null,
  previous: SentSchedule | null,
  dismissed: string | null,
): SentSchedule | null {
  let node: SentSchedule | null = null;
  if (sent && sent.state !== 'active') {
    node = sent;
  } else if (sent && previous && previous.endedBy === 'replaced') {
    node = previous;
  }
  return node && identityOf(node) !== dismissed ? node : null;
}

const read = (storage: () => Storage, key: string): string | null => {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
};

const write = (storage: () => Storage, key: string, value: string): void => {
  try {
    storage().setItem(key, value);
  } catch {
    // A private window without storage: the report just comes back on reload
  }
};

const dismissKey = (deviceId: string) => `ba85.dismissed.${deviceId}`;
const replacedKey = (deviceId: string) => `ba85.replaced.${deviceId}`;

/** Dismiss is per browser: clients may not write the node (O3) */
export const readDismissed = (deviceId: string): string | null =>
  read(() => window.localStorage, dismissKey(deviceId));

export const storeDismissed = (deviceId: string, identity: string): void =>
  write(() => window.localStorage, dismissKey(deviceId), identity);

/** The action a send replaced, kept by the browser that sent it, keyed by the new identity (C11) */
export function readReplacedAction(deviceId: string, identity: string): string | null {
  try {
    const map = JSON.parse(read(() => window.sessionStorage, replacedKey(deviceId)) || '{}');
    return typeof map[identity] === 'string' ? map[identity] : null;
  } catch {
    return null;
  }
}

export function storeReplacedAction(deviceId: string, identity: string, name: string): void {
  // Only the latest send matters: the next one moves this node on
  write(() => window.sessionStorage, replacedKey(deviceId), JSON.stringify({ [identity]: name }));
}

/** hh:mm in the page's language; a time not from today gets its date in front */
export function formatTime(timestamp: number, language: string, now: number = Date.now()): string {
  const date = new Date(timestamp);
  const today = date.toDateString() === new Date(now).toDateString();
  return new Intl.DateTimeFormat(language, {
    ...(today ? {} : { month: 'short', day: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}
